<script lang="ts">
import { OrSidebarV3, OrDrawerV3, OrSelectV3, useResponsive, OrMenuItemV3, OrTooltipV3 } from '@onereach/ui-components';
import { useEventBus, createReusableTemplate } from '@vueuse/core';
import { defineComponent, ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';

import { useCheckPermission } from '@/composables';
import useI18n from '@/composables/useI18n.ts';
import * as allMenuItems from '@/constants/menuItems.ts';

const [DefineNavigationItems, ReuseNavigationItems] = createReusableTemplate();

export default defineComponent({
  name: 'OrNavBar',
  components: {
    OrSidebarV3,
    OrDrawerV3,
    OrSelectV3,
    OrMenuItemV3,
    OrTooltipV3,
    DefineNavigationItems,
    ReuseNavigationItems,
  },
  setup() {
    const { isMobile } = useResponsive();
    const isOpen = ref(false);
    const { canAccessRoute } = useCheckPermission();
    const { t: translate } = useI18n();

    const sidebarVariant = computed(() => isMobile.value ? 'OrDrawerV3' : 'OrSidebarV3');

    const menuItems = computed(() => {
      const allItems = allMenuItems.getAdminMenuItems();
      return allItems.filter((item) => canAccessRoute(item.to));
    });

    const menuGroupItems = computed(() => {
      return [...new Set(menuItems.value.map(item => item.group))];
    });

    const filterMenuItemsByGroup = (group?: string) => {
      return menuItems.value.filter(item => group ? item.group === group : item.group === '');
    };

    const toggleSidebar = (value: boolean) => isOpen.value = value;

    watch(isMobile, (value) => {
      if (!value && isOpen.value) isOpen.value = false;
    });

    const { on, off } = useEventBus<boolean>('sidebar:open-drawer');

    onMounted(() => on(toggleSidebar));
    onBeforeUnmount(() => off(toggleSidebar));

    // Helper to resolve translation keys for group
    const groupLabels = computed(() => {
      const labels: Record<string, string> = {};
      menuGroupItems.value.forEach((group: string) => {
        labels[group] = group ? translate(`sidebar.group.${group}`) : '';
      });
      return labels;
    });
    const menuItemLabels = computed(() => {
      const labels: Record<string, string> = {};
      menuItems.value.forEach((item) => {
        labels[item.label] = translate(item.label);
      });
      return labels;
    });

    return {
      isMobile,
      sidebarVariant,
      isOpen,
      menuGroupItems,
      toggleSidebar,
      filterMenuItemsByGroup,
      groupLabels,
      menuItemLabels,
    };
  },
});
</script>

<template>
  <DefineNavigationItems v-slot="{ isCollapsed }">
    <template
      v-for="(group, groupInd) in menuGroupItems"
      :key="group"
    >
      <div
        v-if="!isCollapsed"
        class="typography-caption-regular text-outline dark:text-outline-dark px-lg py-sm md:px-sm"
      >
        {{ groupLabels[group] }}
      </div>
      <div
        v-else
        class="flex items-center typography-caption-regular text-outline dark:text-outline-dark py-sm before:content-['\200B']"
      >
        <hr
          v-if="groupInd > 0"
          class="w-full border-t-1 border-outline-variant dark:border-outline-variant-dark"
        >
      </div>
      <!-- eslint-disable-next-line vue/no-undef-components -->
      <RouterLink
        v-for="menuItem in filterMenuItemsByGroup(group)"
        v-slot="{ isExactActive }"
        :key="menuItem.label"
        :to="menuItem.to"
      >
        <OrTooltipV3
          :content="isCollapsed ? menuItemLabels[menuItem.label] : ''"
          :placement="'right'"
          :debounce="0"
          class="flex w-full"
        >
          <OrMenuItemV3
            :class="[
              'w-full text-on-background dark:text-on-background-dark md:hover:rounded-md',
              isExactActive
                ? '!typography-body-2-bold bg-primary-opacity-0-08 dark:bg-primary-opacity-0-08-dark md:rounded-md'
                : '!typography-body-2-regular',
              isCollapsed
                ? '!gap-0 before:content-[\'\\200B\']'
                : '',
            ]"
            :icon="{ icon: menuItem.icon, variant: isExactActive ? 'filled' : 'outlined' }"
            :selected="isExactActive"
            @click="toggleSidebar(false)"
          >
            <template v-if="!isCollapsed">
              {{ menuItemLabels[menuItem.label] }}
            </template>
          </OrMenuItemV3>
        </OrTooltipV3>
      </RouterLink>
    </template>
  </DefineNavigationItems>

  <component
    :is="sidebarVariant"
    class="w-full"
    :resizeable="false"
    placement="left"
    :width="255"
    collapsible
    :is-open="isMobile ? isOpen : true"
    :padding="isMobile ? false : undefined"
    data-test="admin-items-sidebar"
    @close="toggleSidebar(false)"
  >
    <div class="layout-column">
      <ReuseNavigationItems />
    </div>
    <template #collapsedContent>
      <div class="gap-sm">
        <ReuseNavigationItems :is-collapsed="true" />
      </div>
    </template>
  </component>
</template>
